import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { OrganisationService } from "../../organisation/organisation.service";
import { ActivatedRoute, Router } from "@angular/router";
import { LinqsetRepositoryService } from "../../model-services/linqset-repository.service";
import { GraphComponentComponent } from "../../canvas/graph-component/graph-component.component";
import { MatSidenav, MatSidenavContainer } from "@angular/material/sidenav";
import { GraphProcessingService } from "../../model-services/graph-processing.service";
import { createInsightsFilter, createOrganisation, FREE_NODES, getCurrency, ModelDetails, Organisation, Tag, TAG_GROUP_ALL_MODELS, TagGroup } from "../../common/organisation";
import { CREATE_MODEL_WITH_AI_URL, DEFAULT_URL, SKETCH_URL } from "../../app-urls";
import { UserService } from "../../user/user.service";
import { OnboardingService } from "../../user/onboarding.service";
import { ONBOARDING_CHOICE } from "../../user/onboarding";
import { NewModelDialogComponent } from "../../help/popups/new-model-dialog";
import { USER_ROLE, User } from "../../common/user";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ComponentCanDeactivate } from './pending-changes.guard';
import { HostListener } from '@angular/core';
import { Observable, Subscription } from "rxjs";
import { CHANGE_TYPE, getNumberOfChanges } from "../../common/linqset-change-model";
import { CURRENT_STATE_PROJECT_KEY, Linqset, LinqsetNode, MODEL_TYPE, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY, showTagsForModelType, TUTORIAL_MODEL_KEY } from "../../common/linqset-model";
import { LabelService } from "../../model-services/label.service";
import { ChangeEvent } from "../../model-components/change-event";
import { MiniGraphType } from "../../canvas/mini-graph/mini-graph.component";
import { FUNCTION, FunctionService } from "../../model-services/function.service";
import { FilterService } from "../../model-services/filter.service";
import { AppComponent } from "../../app.component";
import { processLinqset, createLinqset } from "../../common/model-processing";
import { ConfirmDialogComponent, ConfirmDialogModel } from "../../ui-components/dialogs/confirm-dialog.component";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { SCREEN } from "../layouts-enums";
import { DEFAULT_LEFT_PANE_WIDTH, MAX_LEFT_PANE_WIDTH, MAX_RIGHT_PANE_WIDTH, MIN_CANVAS_WIDTH, MIN_PANE_WIDTH, SidenavService } from "./sidenav.service";
import { LinqsetNodeVisualisation } from "../../canvas/model/graph-model";
import { GraphService } from "../../canvas/graph.service";
import { HelpOverlay, HelpService } from "../../help/help-services";
import { NodePropertiesComponent } from "../../model-components/nodes/node-properties.component";
import { IEdge, IModelItem, INode } from "@yfiles/yfiles";
import { CdkConnectedOverlay, ConnectionPositionPair } from "@angular/cdk/overlay";
import { NodeDialogComponent } from "../../model-components/nodes/node-dialog.component";
import { ModelService } from "../../organisation/models/model-service";
import { IMAGE_LINQ } from "../../model-services/model-item-enums";
import { ExceptionAndLoggingService } from "../../linq-utils/logging/exception-logging-service";

const CHANGES_PROMPT_NUMBER = 50
const MAP_COLLAPSED_KEY = "linq.modelEditor.mapCollapsed"

enum RIGHT_PANEL {
    TEMPORAL,
    INSIGHTS
}

enum LEFT_PANEL {
    NONE,
    NODE,
    EDGE,
    MODEL,
    GROUP
}

@Component({
    selector: 'app-model-layout',
    templateUrl: './model-layout.html',
    styleUrls: ['./model-layout.scss'],
    standalone: false
})
export class ModelLayoutComponent implements OnInit, ComponentCanDeactivate, OnDestroy {
    IMAGE_LINQ = IMAGE_LINQ
    screen:SCREEN = SCREEN.MODEL
    organisation:Organisation
    organisationLinqset:Linqset
    linqset: Linqset
    loading = true
    saving = false

    sidebarWidth:number

    numberChanges:number = CHANGES_PROMPT_NUMBER
    changesDialogRef:MatDialogRef<ConfirmDialogComponent>

    organisationKey:string
    modelKey:string
    projectKey:string
    reloadorg:boolean = false
    nodeKey:string      // can set a node key as an param and so select that node
    modelType:MODEL_TYPE = MODEL_TYPE.PROCESS
    helpGuides:string[] = []        // the help guides about this screen
    welcome:boolean = false

    modelLinqset:Linqset    // used to represent a selected model node insight data
    compareModelLinqset:Linqset    // used to represent a selected model's current state insight data

    comparison:Linqset
    // the model in this project shown over the same model in current state: what the project removed is drawn in grey,
    // as the insights screen compares them. Read-only while shown, as those nodes aren't the project's to change
    comparing = false
    graphTrigger:any = {}
    private readonlyBeforeComparing:boolean|undefined

    // a current state model this project has changed, which so has a current state to compare with
    get canCompare() {
        const l = this.linqset
        return !!l && !!this.comparison && l.isProjectModel() && !l.isOrganisationModel() && l.projectModelKeys.includes(l.modelKey)
    }

    private startComparing() {
        if (!this.canCompare) return
        // the comparison is of the whole model, as drawn in its process view
        if (this.modelType !== MODEL_TYPE.PROCESS) {
            this.modelType = MODEL_TYPE.PROCESS
            this.linqset.modelType = MODEL_TYPE.PROCESS
        }
        this.linqset.addRemovedNodesToLinqset(this.comparison)
        this.readonlyBeforeComparing = this.linqset.readonlyOverride
        this.linqset.readonlyOverride = true
        this.readOnlyGraph = true
        this.comparing = true
        this.graphTrigger = {}
    }

    // back to the project's model alone; redrawn unless another view is about to be drawn anyway
    private stopComparing(redraw = true) {
        this.linqset.removeRemovedNodesToLinqset()
        this.linqset.readonlyOverride = this.readonlyBeforeComparing
        this.readOnlyGraph = this.linqset.readonly
        this.comparing = false
        if (redraw) this.graphTrigger = {}
    }

    user:User
    selectedTagGroup:TagGroup = null
    isGrouped:boolean
    selectedMiniGraph:MiniGraphType = MiniGraphType.OVERVIEW
    selectedNodes:INode[]
    selectedEdge:IEdge
    readOnlyGraph:boolean = false
    selectionTrigger:any = {}           // used to notify when a selection changes


    routeSub:Subscription
    querySub:Subscription
    linqsetLoadedSub:Subscription
    filterSubscription:Subscription
    editModelSubscription:Subscription

    userOpensPanel:boolean          // used to defer loading on insights data
    mapCollapsed:boolean = this.loadMapCollapsed()     // left panel map hidden to give the properties more room

    rightPanel:RIGHT_PANEL = RIGHT_PANEL.INSIGHTS
    leftPanel:LEFT_PANEL = LEFT_PANEL.NONE

    MODEL_TYPE=MODEL_TYPE
    FUNCTION=FUNCTION
    RIGHT_PANEL=RIGHT_PANEL
    LEFT_PANEL=LEFT_PANEL

    /**
        * This stores the state of the resizing event and is updated
        * as events are fired.
    */
    resizingEvent = {
        left:true,
        isResizing: false,
        startingCursorX: 0,
        startingWidth: 0,
    };

    overlayTop: ConnectionPositionPair = {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
    };

    @ViewChild(GraphComponentComponent) gcComponent: GraphComponentComponent
    @ViewChild('rightdrawer') public rightDrawer: MatSidenav;
    @ViewChild('leftdrawer') public leftDrawer: MatSidenav;
    @ViewChild(MatSidenavContainer, {read: ElementRef}) sidenavContainerRef: ElementRef<HTMLElement>
    @ViewChild(CdkConnectedOverlay) tagsList:CdkConnectedOverlay

    @ViewChild(NodePropertiesComponent) nodeComponent: NodePropertiesComponent

    MiniGraphType = MiniGraphType

    constructor(public organisationService:OrganisationService, private router: Router, private labelService:LabelService, private filterService:FilterService,
        private changeDetectorRef: ChangeDetectorRef, public linqsetRepo: LinqsetRepositoryService, private route:ActivatedRoute,
        private functionService:FunctionService, private dialog:MatDialog, private graphService:GraphService, public app:AppComponent, private snackBar: MatSnackBar,
        private graphProcessingService:GraphProcessingService, private userService:UserService, public sidenavService:SidenavService, private helpService:HelpService,
        private modelService:ModelService, private exceptionService:ExceptionAndLoggingService, private onboardingService:OnboardingService, private zone:NgZone) {

            this.helpGuides = ['models', 'linq-language', 'work-and-flow', 'roles-systems-people-infrastructure']
    }

    ngOnInit(): void {
        if (!this.routeSub) {
            this.querySub = this.route.queryParams.subscribe(async p => {
                this.nodeKey = p['nodeKey']
                this.reloadorg = p['reloadorg'] && p['reloadorg']==='true'
                this.welcome = p['welcome'] === 'true'                                // getting started has just built this model
            })

            this.routeSub = this.route.params.subscribe(async p => {
                this.linqset = undefined
                this.organisationKey = p['organisation_key']
                this.modelKey = p['model_key']
                this.modelType = p['model_type'] as MODEL_TYPE
                this.sidenavService.setSidenavWidths(this.modelType, this.modelKey)
                this.projectKey = p['project_key']

                if (this.gcComponent?.graphComponent)
                    this.graphService.removeHeatmap(this.gcComponent.graphComponent)

                this.linqset = await this.loadModelData()
                if (this.linqset) {
                    this.changeDetectorRef.detectChanges()
                    this.postLoad()
                }
            })
        }

        if (!this.editModelSubscription) {
            this.editModelSubscription = this.graphProcessingService.changeEvent.subscribe(async e => {
                this.handleEditModelGraphEvent(e)
            })
        }

        if (!this.linqsetLoadedSub) {
            this.linqsetLoadedSub = this.functionService.linqsetLoadedEvent.subscribe(async e => {
                this.loading = true
                this.changeDetectorRef.detectChanges()

                try {
                    const runInsights = (e === "runInsights")
                    const loadOrganisation = (e === "loadOrganisation")
                    this.linqset = await this.loadLinqsets(runInsights, loadOrganisation)
                }catch(err) {
                    this.router.navigateByUrl(DEFAULT_URL(this.organisation?._key))
                }finally {
                    this.loading = false
                }
                this.changeDetectorRef.detectChanges()

            })
        }

        if (!this.filterSubscription) {
            this.filterSubscription = this.filterService.filterEvent.subscribe(async e => {
                if (this.linqset && this.gcComponent?.graphComponent && this.organisationLinqset) {
                    this.linqset.filter.nodeTags = this.filterService.getTagKeys()
                    this.linqset.filterNodes = this.filterService.getNodes()

                    this.linqset.filter.nodeKeys = this.linqset.filterNodes.map(n => n._key)

                    this.graphProcessingService.processGraph(this.organisation, this.gcComponent.graphComponent.graph, this.linqset, this.organisationLinqset, false)
                }
            })
        }

    }

    ngOnDestroy(): void {
        // a screen tour points at this page, so it ends when the page goes
        this.helpService.endTour()
        if (this.routeSub) this.routeSub.unsubscribe()
        if (this.querySub) this.querySub.unsubscribe()
        if (this.linqsetLoadedSub) this.linqsetLoadedSub.unsubscribe()
        if (this.filterSubscription) this.filterSubscription.unsubscribe()
        if (this.editModelSubscription) this.editModelSubscription.unsubscribe()
    }

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate(): Observable<boolean> | boolean {
        // the model's insights, if it changed; with no model loaded yet there is nothing to refresh (without one the
        // repository would refresh the whole organisation)
        if (this.linqset) this.linqsetRepo.refreshInsights(this.linqset)

        return !this.linqset || !this.linqset.isDirty()
        // insert logic to check if there are pending changes here;
        // returning true will navigate without confirmation
        // returning false will show a confirm dialog before navigating away
    }

    @HostListener('document:keydown', ['$event'])
    handleTheKeyboardEvent(event: KeyboardEvent) {
        // Ctrl+S on Windows/Linux, Cmd+S on Mac
        if (!this.readOnlyGraph && (event.key === "s" || event.key === "S") && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            this.functionService.executeFunction(FUNCTION.SAVE_MODEL)
            return
        }

        // single-key shortcuts must not fire while typing in a field, a label editor or an open dialog/menu
        if (event.ctrlKey || event.metaKey || event.altKey || this.isTypingTarget(event.target) || this.app.smallScreen) return
        if (document.querySelector('.cdk-overlay-container .mat-mdc-dialog-container, .cdk-overlay-container .mat-mdc-menu-panel')) return

        if (event.key === "[") {
            event.preventDefault()
            this.toggleLeftPane()
        }else if (event.key === "]") {
            event.preventDefault()
            this.toggleRightPane()
        }else if (event.key === "f" || event.key === "F") {
            event.preventDefault()
            this.gcComponent?.graphComponent?.fitGraphBounds()
        }
    }

    private isTypingTarget(target:EventTarget|null) {
        const el = target as HTMLElement
        if (!el || !el.tagName) return false
        return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
    }

    isOrganisation() {return !this.modelKey || this.modelKey === ORGANISATION_MODEL_KEY}
    isTutorial() {return this.modelKey && this.modelKey === TUTORIAL_MODEL_KEY}
    hasOrgWriteAccess() {return this.organisation?.role && this.organisation.role !== USER_ROLE.READ}

    modelName() {
        if (!this.linqset) return ""
        if (this.linqset.model.projectKey!==CURRENT_STATE_PROJECT_KEY) return "Project: " + this.linqset.model.name
        return this.linqset.model.name
    }

    // a model getting started built: say what AI did, then show the user around their own model. Once only, so a
    // refresh doesn't repeat it
    private welcomeModel() {
        this.welcome = false
        this.router.navigate([], {relativeTo:this.route, queryParams:{welcome:null}, queryParamsHandling:'merge', replaceUrl:true})
        this.onboardingService.firstModelOpened()
        this.helpService.openAIModelIntroductionDialog(this.app.smallScreen).afterClosed().subscribe(() => {
            if (!this.app.smallScreen) this.showWhatsThisHelp()
        })
    }

    async postLoad() {
        if (this.linqset.isTutorial()) {
            this.rightPanel = RIGHT_PANEL.INSIGHTS
            if (this.rightDrawer) await this.rightDrawer.open()
            if (this.leftDrawer) await this.leftDrawer.close()
            if (this.tagsList?.overlayRef) this.tagsList.overlayRef.updatePosition()

            return
        }

        // models open on the palette. AI is offered when a model is created, so an empty one isn't met by the AI panel:
        // it stays in the toolbar for later
        this.rightPanel = RIGHT_PANEL.INSIGHTS
        if (this.rightDrawer) await this.rightDrawer.close()
        if (this.leftDrawer) await this.leftDrawer.open()
        if (this.tagsList?.overlayRef) this.tagsList.overlayRef.updatePosition()

        if (this.linqset.nodes.length > 0 || this.linqset.isOrganisationModel()) {
            this.selectRequestedNode()
            if (this.welcome) this.welcomeModel()
            return
        }

        // an empty model is met with the ways to start it, each time, by someone who can change it. The screen's help
        // follows drawing it only the first time
        const first = this.onboardingService.firstModelOpened()
        if (this.linqset.readonly) return
        NewModelDialogComponent.open(this.dialog).afterClosed().subscribe(choice => {
            // building it with AI is the toolbar's Build model with AI
            if (choice === ONBOARDING_CHOICE.CREATE) this.handleFunctionEvent({function:FUNCTION.OPEN_MODELLING_AI})
            else if (choice === ONBOARDING_CHOICE.TOUR) this.handleFunctionEvent({function:FUNCTION.SCREEN_TOUR})
            else if (!this.onboardingService.handleChoice(choice, this.organisationKey) && first) this.showWhatsThisHelp()
        })
    }

    private async loadModelData():Promise<Linqset> {
        try {
            let newLinqset:Linqset

            this.numberChanges = CHANGES_PROMPT_NUMBER
            this.changesDialogRef = undefined
            this.user = this.userService.getUser()
            this.selectedEdge = undefined
            if (this.gcComponent?.graphComponent) this.gcComponent.graphComponent.selection.clear()
            this.selectedNodes = []
            this.leftPanel = LEFT_PANEL.NONE

            this.loading = true

            newLinqset = await this.loadLinqsets(false, this.reloadorg)
            if (!newLinqset) return undefined

            newLinqset.modelType = this.modelType
            // a model loaded afresh is of its workspace alone
            this.comparing = false
            this.readOnlyGraph = newLinqset.readonly

            this.selectedMiniGraph = MiniGraphType.OVERVIEW

            if (newLinqset.isTutorial()) {
                // tutorial
                this.organisation = createOrganisation()
                this.organisation.models = [newLinqset.model]
                this.organisation.model = {_key:ORGANISATION_MODEL_KEY, name:"Walkthrough"} as ModelDetails

                const nodes = newLinqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE || n.superType === NODE_SUPERTYPE.SYSTEM)
                this.organisationLinqset = createLinqset(this.organisation, createInsightsFilter(), {nodes, edges:[]})
            }else {
                this.organisation = this.organisationService.organisation
                this.organisationLinqset = this.linqsetRepo.organisationLinqset
                this.comparison = this.linqsetRepo.comparison
            }
            if (newLinqset.isOrganisationModel())
                this.selectTagGroup(this.organisation.modelTags, newLinqset)
            else if (this.selectedTagGroup && ((this.organisation.modelTags._key === this.selectedTagGroup._key) || (this.selectedTagGroup.modelKeys && this.selectedTagGroup.modelKeys.length > 0 && this.selectedTagGroup.modelKeys.indexOf(TAG_GROUP_ALL_MODELS)<0 && this.selectedTagGroup.modelKeys.indexOf(this.modelKey) < 0))) {
                // clear tag group
                this.selectedTagGroup = undefined
                this.labelService.tagGroup = undefined
            }

            return newLinqset

        }catch(err) {
            this.exceptionService.handleCaughtError(err, "Model layout", "Load linqset", "Exception while loading linqset details", this.userService.getUser(), this.organisation)
            this.router.navigateByUrl(DEFAULT_URL(this.organisation?._key))
        }finally {
            this.loading = false
        }
        return undefined
    }

    private async loadLinqsets(runInsights:boolean, loadOrganisation:boolean):Promise<Linqset> {
        if (this.gcComponent?.graphComponent) this.filterService.clearFilter(this.gcComponent.graphComponent)
        const requiresRefresh = this.linqset?.refresh
        this.changeDetectorRef.detectChanges()  // if we dont do this before retrieving we get a change detection error. not sure why.
        const loadedLinqset = await this.linqsetRepo.loadModelData(this.organisationKey, this.modelKey, this.projectKey, loadOrganisation)
        if (loadedLinqset) {
            if (runInsights) await processLinqset(loadedLinqset, this.organisationLinqset, false, this.organisation)
            // this refreshes the graph so do it after refreshing insights
            loadedLinqset.refresh = requiresRefresh
        }
        return loadedLinqset
    }

    newModel() {
        this.functionService.executeFunction(FUNCTION.CREATE_MODEL)
    }

    exitTutorial() {
        this.router.navigateByUrl(DEFAULT_URL(this.organisationKey))
    }

    showWhatsThisHelp() {
        if (!this.linqset) this.linqset = createLinqset(this.organisation, createInsightsFilter())

        // the tour only points at parts that are on screen, so open the panels and let them finish opening first
        let opening = false
        if (this.rightDrawer && !this.rightDrawer.opened) {
            this.rightDrawer.open()
            this.userOpensPanel = true
            opening = true
        }
        if (this.leftDrawer && !this.leftDrawer.opened) {
            this.leftDrawer.open()
            opening = true
        }

        const elements = [
            new HelpOverlay("menu", "<b>Main menu bar:</b> Navigation, management and search functions. Hover over items to see more detail.", {originX:'center', originY:'bottom', overlayX:'center', overlayY:'top'}),
            new HelpOverlay("toolbar", "<b>Toolbar:</b> Tools for managing the current model. Hover over icons to see more detail.", {originX:'center', originY:'bottom', overlayX:'center', overlayY:'top'}),
            new HelpOverlay("graph", "<b>Model canvas:</b> View and maintain the selected model. Hold alt while dragging the canvas to move the model.", {originX:'center', originY:'top', overlayX:'center', overlayY:'top'}),
            new HelpOverlay("palette", "<b>Node palette:</b> Create new nodes by dragging them from here onto the model canvas. Connect nodes by dragging edges between nodes.", {originX:'center', originY:'bottom', overlayX:'center', overlayY:'top'}),
            new HelpOverlay("mini-graph", "<b>Mini graph:</b> Drag the blue square to navigate around the model. Select the dropdown (top left) to select different views.", {originX:'center', originY:'bottom', overlayX:'center', overlayY:'bottom'}),
            new HelpOverlay("left-drawer-contents", "<b>Properties:</b> If a node is selected the node details can be edited here.", {originX:'center', originY:'top', overlayX:'center', overlayY:'top'}),
            new HelpOverlay("left-panel-button", "<b>Panel button:</b> Open and close the left panel.", {originX:'end', originY:'center', overlayX:'start', overlayY:'center'}),
            new HelpOverlay("mat-mdc-tab-header", "<b>Insights panel:</b> Shows insights for model. Select the tabs to see different information.", {originX:'center', originY:'bottom', overlayX:'center', overlayY:'top'}),
            new HelpOverlay("right-panel-button", "<b>Panel button:</b> Open and close the insights panel.", {originX:'start', originY:'center', overlayX:'end', overlayY:'center'}),
            new HelpOverlay("workspaces-menu", "<b>Workspaces menu:</b> Shows the current workspace. Open it to switch between current state and a project or future state.", {originX:'start', originY:'center', overlayX:'end', overlayY:'center'}),
            new HelpOverlay("models-menu", "<b>Models menu:</b> Shows the current model. Open it to switch to a different model.", {originX:'start', originY:'center', overlayX:'end', overlayY:'center'}),
            new HelpOverlay("save-menu", "<b>Model changes:</b> Shows unsaved changes to the model. Save them from here, or press Ctrl+S.", {originX:'start', originY:'center', overlayX:'end', overlayY:'center'}),
            new HelpOverlay("help-menu", "<b>Help menu:</b> Help videos, tutorials and documentation.", {originX:'start', originY:'center', overlayX:'end', overlayY:'center'}),
            new HelpOverlay("home-button", "<b>Organisation home button:</b> Navigate to the organisation dashboard.", {originX:'end', originY:'center', overlayX:'start', overlayY:'center'})
        ]
        setTimeout(() => this.helpService.showWhatsThisOverlay(elements), opening ? 450 : 0)
    }

    graphItemDoubleClickHandler(item:IModelItem) {
        // the canvas runs outside Angular, so its events arrive outside it; see graphNodesSelected
        if (!NgZone.isInAngularZone()) return this.zone.run(() => this.graphItemDoubleClickHandler(item))
        if (item instanceof INode) {
            if (item.tag && item.tag.model) this.openModel(item.tag.model)
            else if (item.tag && item.tag.group) this.gcComponent.toggleGroupNode(item)
            else NodeDialogComponent.open(this.dialog, this.organisation, this.gcComponent.graphComponent, this.linqset, this.organisationLinqset, item)
        }
    }

    getMiniGraphTypes() {
        const types = Object.keys(MiniGraphType).map(t => MiniGraphType[t])
        if (this.linqset && this.linqset.isOrganisationModel()) return types.filter(t => t !== MiniGraphType.DOWNSTREAM && t !== MiniGraphType.UPSTREAM)
        else return types
    }

    selectMiniGraphType(type:MiniGraphType) {
        this.selectedMiniGraph = type
    }

    toggleMap() {
        this.mapCollapsed = !this.mapCollapsed
        try {
            localStorage.setItem(MAP_COLLAPSED_KEY, JSON.stringify(this.mapCollapsed))
        }catch(err) {
            // storage unavailable; the choice just won't persist
        }
    }

    private loadMapCollapsed():boolean {
        try {
            return localStorage.getItem(MAP_COLLAPSED_KEY) === "true"
        }catch(err) {
            return false
        }
    }

    // panel widths are preferences, clamped so the two panels never squeeze the canvas below MIN_CANVAS_WIDTH
    private availablePanelWidth() {
        // the container is narrower than the window (scrollbar, margins), so measure it when it exists
        const containerWidth = this.sidenavContainerRef?.nativeElement.clientWidth || window.innerWidth
        return containerWidth - MIN_CANVAS_WIDTH
    }

    private clampWidth(width:number, max:number) {
        return Math.max(MIN_PANE_WIDTH, Math.min(width, max))
    }

    getLeftPanelWidth() {
        const max = Math.min(MAX_LEFT_PANE_WIDTH, this.availablePanelWidth() - (this.rightDrawer?.opened?MIN_PANE_WIDTH:0))
        return this.clampWidth(this.sidenavService.leftPaneWidth, max)
    }

    getRightPanelWidth() {
        let width:number
        // the tutorials panel leaves the canvas room to practise on
        if (this.isTutorial()) width = 560
        else if (this.rightPanel === RIGHT_PANEL.TEMPORAL) width = 600
        else width = this.sidenavService.rightPaneWidth

        const max = this.availablePanelWidth() - (this.leftDrawer?.opened?this.getLeftPanelWidth():0)
        return this.clampWidth(width, max)
    }

    // when there is not room for both panels at their minimum width, opening one closes the other
    panelOpening(left:boolean) {
        if (this.availablePanelWidth() >= 2*MIN_PANE_WIDTH) return
        const other = left?this.rightDrawer:this.leftDrawer
        if (other?.opened) other.close()
    }

    @HostListener('window:resize')
    handleWindowResize() {
        if (this.leftDrawer?.opened && this.rightDrawer?.opened) this.panelOpening(false)
    }

    openModel(model:ModelDetails) {
        if (model.role >= 0)
            this.router.navigateByUrl(SKETCH_URL(this.organisation._key, model._key,MODEL_TYPE.PROCESS, this.projectKey))
        else
            this.snackBar.open("You do not have access to this model. You will need to request access from the owner.", "close");
    }

    getModelInsights(model:ModelDetails) {
        return model.insights?model.insights.find(i => i.projectKey === this.linqset.workspaceKey):undefined
    }

    addExternalNode(node:LinqsetNode) {
        // create a visualisation of the model that is being connected
        const model = this.organisationService.organisation.models.find(m => m._key === node.modelKey)
        this.gcComponent.addExternalNode(node, model)
    }

    async handleEditModelGraphEvent(event:ChangeEvent) {
        if (event.type === CHANGE_TYPE.DELETED) this.graphNodesSelected(event)
        await this.graphProcessingService.handleEditModelGraphEvent(this.organisation, this.gcComponent.graphComponent, this.linqset, this.organisationLinqset, event)

        const changes = getNumberOfChanges(this.linqset.changes)

        if (!this.changesDialogRef && changes > this.numberChanges) {
            const message = `You have ${changes} unsaved changes. Save them now?`;
            const dialogData = new ConfirmDialogModel("Unsaved changes", [message], undefined, false, "Save", "Not now");

            this.changesDialogRef = this.dialog.open(ConfirmDialogComponent, {
                maxWidth: "500px",
                data: dialogData
            });

            this.changesDialogRef.afterClosed().subscribe(async dialogResult => {
                if (dialogResult) {
                    this.functionService.executeFunction(FUNCTION.SAVE_MODEL)
                }
                this.changesDialogRef = undefined
                this.numberChanges = getNumberOfChanges(this.linqset.changes) + CHANGES_PROMPT_NUMBER
            });

        }

        if (event.nodes && event.nodes.length > 0) {
            if (event.type === CHANGE_TYPE.ADDED) {
                if (this.organisation.numNodes <= FREE_NODES && (this.organisation.numNodes + event.nodes.length > FREE_NODES)) {
                    const currency = getCurrency(this.organisation.currencyCode)

                    this.snackBar.open(`You have exceeded the limit on free nodes. If you remain over the limit at the end of the month you will be billed ${currency.nodePrice} per node above the limit ${FREE_NODES}.`, 'close')
                }
                this.organisation.numNodes += event.nodes.filter(n=> !n.external).length

            }else if (event.type === CHANGE_TYPE.DELETED) {
                this.organisation.numNodes -= event.nodes.filter(n=> !n.external).length
            }
        }
    }

    selectNodeForVisualisationKey(visual:LinqsetNodeVisualisation) {
        if (this.gcComponent) {
            const node = (visual.visualisationKey && visual.visualisationKey.length > 0)?
                this.gcComponent.graphComponent.graph.nodes.find(n => n.tag.visualisationKey === visual.visualisationKey):
                this.gcComponent.graphComponent.graph.nodes.find(n => n.tag.node._key === visual.node._key)

            if (node) {
                this.graphService.selectModelItems(this.gcComponent.graphComponent, [node])
                this.graphNodesSelected()
            }
        }
    }

    zoomToModelItem() {
        this.graphService.zoomToModelItem(this.gcComponent.graphComponent)
    }

    // a link can ask for a node (such as a todo) to be selected. The graph may be built more than once while the
    // model loads, and each build clears the selection, so wait until the node's graph item is stable and re-select
    // if a later build wipes it
    private async selectRequestedNode() {
        const nodeKey = this.nodeKey
        if (!nodeKey) return
        this.nodeKey = undefined

        const wait = (ms:number) => new Promise(resolve => setTimeout(resolve, ms))
        const findItem = () => this.gcComponent?.graphComponent?.graph.nodes.find(gn => gn.tag?.node?._key === nodeKey)

        for (let attempt = 0; attempt < 30; attempt++) {
            const item = findItem()
            // a layout still running would refit the view after the zoom
            if (item && this.graphService.layoutsRunning === 0) {
                await wait(300)
                if (findItem() === item && this.graphService.layoutsRunning === 0) {
                    this.handleSelectNodesEvent([nodeKey])
                    await wait(600)
                    if (this.gcComponent?.graphComponent?.selection.includes(findItem())) return
                }
            }else await wait(200)
        }
    }

    handleSelectNodesEvent(nodeKeys:string[]) {
        this.graphService.selectModelItems(this.gcComponent.graphComponent, nodeKeys.reduce((a,k) => {
            const nodes = this.gcComponent.graphComponent.graph.nodes.filter(gn => {
                if (gn.tag.node) return gn.tag.node._key === k
                if (gn.tag.model) return gn.tag.model._key === k
                if (gn.tag.group) return gn.tag.group._key === k
                return false
                }).toArray()
            return a.concat(nodes)
        }, []))

        this.graphNodesSelected()
    }

    graphNodesSelected(event?:ChangeEvent) {
        // the canvas runs outside Angular, so a selection arrives outside it. The panels it opens (node, edge, group
        // and model properties) must be made inside it, or their own clicks run outside it too and nothing on them
        // updates until something else does, such as a field losing focus
        if (!NgZone.isInAngularZone()) return this.zone.run(() => this.graphNodesSelected(event))
        if (this.gcComponent?.graphComponent) {
            this.leftPanel = LEFT_PANEL.NONE

            // if event it means those edges/nodes are deleted and do not include them in selection
            const edges = event && event.items?this.gcComponent.graphComponent.selection.edges.filter(e => event.items.indexOf(e) < 0).toArray():this.gcComponent.graphComponent.selection.edges.toArray()
            this.selectedNodes = event && event.items?this.gcComponent.graphComponent.selection.nodes.filter(n => event.items.indexOf(n) < 0).toArray():this.gcComponent.graphComponent.selection.nodes.toArray()
            if (this.selectedNodes.length===0 && edges.length===1) {
                this.selectedMiniGraph = MiniGraphType.ADJACENT
                this.selectedEdge = this.gcComponent.graphComponent.selection.edges.first()
            }else {
                this.selectedEdge = undefined
            }
            if (this.selectedNodes && this.selectedNodes.length > 0 && this.selectedNodes[0].tag.model) {
                const filter = createInsightsFilter(this.linqset.workspaceKey)
                filter.modelKeys = [this.selectedNodes[0].tag.model._key]
                this.modelLinqset = createLinqset(this.organisationService.organisation, filter)
                if (!this.linqset.isCurrentState() && this.modelLinqset.model.projectKey===CURRENT_STATE_PROJECT_KEY) {
                    const cfilter = createInsightsFilter(CURRENT_STATE_PROJECT_KEY)
                    cfilter.modelKeys = [this.selectedNodes[0].tag.model._key]
                    this.compareModelLinqset = createLinqset(this.organisationService.organisation, cfilter)
                }else this.compareModelLinqset = undefined
            }
            if (this.nodeComponent) this.nodeComponent.nodes = this.selectedNodes

            // we use this value, so node properties component gets updated selected nodes before it is removed from the screen
            if (this.selectedNodes.length > 0 && this.selectedNodes[0].tag) {
                if (this.selectedNodes[0].tag.node) this.leftPanel = LEFT_PANEL.NODE
                else if (this.selectedNodes[0].tag.model) this.leftPanel = LEFT_PANEL.MODEL
                else if (this.selectedNodes[0].tag.group) this.leftPanel = LEFT_PANEL.GROUP
            }else if (this.selectedEdge) {
                this.leftPanel = LEFT_PANEL.EDGE
            }
            this.changeDetectorRef.detectChanges()
            this.selectionTrigger = {}
        }
    }

    // the toolbar colour signals the model's state; the labels say it in words, since colour alone isn't enough
    getToolbarStates():{key:string, label:string, icon:string, tooltip:string}[] {
        const states = []
        if (this.filterService.isFiltered) states.push({key:'filtered', label:'Filtered', icon:'filter_alt', tooltip:'A filter is applied, so only part of the model is shown'})
        if (this.linqset && !this.linqset.isCurrentState()) states.push({key:'project', label:this.linqset.project?.name?'Project: ' + this.linqset.project.name:'Project', icon:'construction', tooltip:(this.linqset.project?.name?'Project ' + this.linqset.project.name + '. ':'') + 'Changes are made in the project and do not affect current state until merged'})
        if (this.readOnlyGraph) states.push({key:'readonly', label:'Read-only', icon:'lock', tooltip:'You can view but not change this model'})
        return states
    }

    getToolbarMode() {
        return this.getToolbarStates()[0]?.key || 'default'
    }

    async toggleLeftPane() {
        await this.leftDrawer.toggle()
        if (this.tagsList?.overlayRef) this.tagsList.overlayRef.updatePosition()
    }

    toggleRightPane() {
        this.userOpensPanel = true
        this.rightPanel = RIGHT_PANEL.INSIGHTS
        this.rightDrawer.toggle()
    }

    editModelDetails(isNew:boolean) {
        this.modelService.openModelDialog(isNew?null:this.linqset.model, this.linqset.project).subscribe(async result => {
            if (result)  {
                this.loading = true
                try {
                    const model =await this.modelService.saveModel(result, this.linqset.workspaceKey, isNew)
                    if (isNew || result.file) {
                        await this.linqsetRepo.loadModelData(this.organisation._key, undefined, this.linqset.workspaceKey, true)
                    }
                    if (!isNew && result.file)
                        this.linqset = await this.loadLinqsets(false, false)
                    if (isNew) this.router.navigateByUrl(SKETCH_URL(this.organisation._key, model._key, MODEL_TYPE.PROCESS, this.linqset.workspaceKey))
                }catch(err) {
                    // the error has already been shown
                }finally {
                    this.loading = false
                }
            }
        })
    }

    public async handleFunctionEvent(event:{function:FUNCTION, value?:any}) {
        this.saving = true

        try {
            if (event.function === FUNCTION.TOGGLE_TEMPORAL_MODEL) {
                this.rightPanel = this.rightPanel === RIGHT_PANEL.TEMPORAL?RIGHT_PANEL.INSIGHTS:RIGHT_PANEL.TEMPORAL
                if (this.rightPanel === RIGHT_PANEL.TEMPORAL) {
                    if (!this.rightDrawer.opened) this.rightDrawer.open()
                    if (this.leftDrawer.opened) this.leftDrawer.close()
                }else {
                    if (this.rightDrawer.opened) this.rightDrawer.close()
                    if (!this.leftDrawer.opened) this.leftDrawer.open()
                }
                return
            }

            if (event.function === FUNCTION.OPEN_MODELLING_AI) {
                // AI builds an empty model with the wizard, as it does a new model
                this.router.navigateByUrl(CREATE_MODEL_WITH_AI_URL(this.organisation._key, this.linqset.workspaceKey, this.linqset.modelKey))
                return
            }

            if (event.function === FUNCTION.EDIT_MODEL_DETAILS) {
                this.editModelDetails(false)
                return
            }
            if (event.function === FUNCTION.CREATE_MODEL) {
                const start = await this.functionService.askHowToStartModel()
                if (start === 'ai') this.router.navigateByUrl(CREATE_MODEL_WITH_AI_URL(this.organisation._key, this.linqset.workspaceKey))
                else if (start === 'blank') this.editModelDetails(true)
                return
            }

            if (event.function === FUNCTION.SCREEN_TOUR) {
                this.showWhatsThisHelp()
                return
            }

            if (event.function === FUNCTION.COMPARE_WITH_CURRENT_STATE) {
                if (this.comparing) this.stopComparing()
                else this.startComparing()
                return
            }

            if (event.function === FUNCTION.CHANGE_MODEL_TYPE) {
                // another view of the model is of the project alone
                if (this.comparing) this.stopComparing(false)
                this.modelType = event.value
                this.linqset.modelType = this.modelType
                this.readOnlyGraph = this.linqset.readonly
                if (this.tagsList?.overlayRef) this.tagsList.overlayRef.updatePosition()

                return
            }

            if (event.function === FUNCTION.CREATE_GROUPS) this.isGrouped = true
            if (event.function === FUNCTION.REMOVE_GROUPS) this.isGrouped = false

            await this.functionService.executeFunction(event.function, event.value, this.linqset, this.linqset.project, this.gcComponent.graphComponent, this.gcComponent, this.selectedTagGroup)
            this.linqset = this.linqsetRepo.linqset
        }catch(err) {
            console.error(err)
        }finally {
            this.saving = false
        }
    }

    showTagGroup() {
        return this.selectedTagGroup && showTagsForModelType(this.modelType)
    }

    isHelpOn() {
        return AppComponent.help
    }


    handleTagListEvent(event?:Tag) {
        if (!event) {
            this.selectTagGroup(undefined, this.linqset)
        }
    }

    selectTagGroup(tg:TagGroup, linqset:Linqset) {
        this.selectedTagGroup = tg
        this.labelService.tagGroup = tg
        // on a small screen the canvas is only drawn once the model has loaded, and labels it then with this tag group
        if (!this.gcComponent?.graphComponent) return
        this.labelService.updateLabelsForGraph(this.gcComponent.graphComponent.graph, false, linqset, undefined)
        this.gcComponent.resetGraph(false)
    }

    startResizing(event: MouseEvent, left:boolean): void {
        this.resizingEvent = {
            isResizing: true,
            left,
            startingCursorX: event.clientX,
            startingWidth: left?this.getLeftPanelWidth():this.getRightPanelWidth(),     // rendered (clamped) width so the drag doesn't jump
        };
    }

    /*
    * This method runs when the mouse is moved anywhere in the browser
    */
    @HostListener('window:mousemove', ['$event'])
    updateSidenavWidth(event: MouseEvent) {
        // No need to even continue if we're not resizing
        if (!this.resizingEvent.isResizing) {
            return;
        }

        // 1. Calculate how much mouse has moved on the x-axis
        const cursorDeltaX = event.clientX - this.resizingEvent.startingCursorX;

        // 2. Calculate the new width according to initial width and mouse movement
        if (this.resizingEvent.left) {
            const v = this.resizingEvent.startingWidth + cursorDeltaX
            if (v < MIN_PANE_WIDTH) {
                this.leftDrawer.toggle()
                this.sidenavService.leftPaneWidth = DEFAULT_LEFT_PANE_WIDTH
                this.stopResizing()
            }else if (v > MAX_LEFT_PANE_WIDTH) {
                this.stopResizing()
            }else this.sidenavService.leftPaneWidth = this.resizingEvent.startingWidth + cursorDeltaX;
        }else {
            const v = this.resizingEvent.startingWidth - cursorDeltaX
            if (v < MIN_PANE_WIDTH) {
                this.rightDrawer.toggle()
                this.sidenavService.rightPaneWidth = this.sidenavService.defaultRightPaneWidth()
                this.stopResizing()
            }else if (v > MAX_RIGHT_PANE_WIDTH) {
                this.stopResizing()
            }else this.sidenavService.rightPaneWidth = v;
        }
        this.changeDetectorRef.detectChanges()
    }

    @HostListener('window:mouseup')
    stopResizing() {
        if (this.resizingEvent.isResizing) this.sidenavService.saveWidths()
        this.resizingEvent.isResizing = false;
        if (this.tagsList?.overlayRef) this.tagsList.overlayRef.updatePosition()
    }
}
